import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const WORDS = ['Orchestrator', 'MCP', 'Database', 'Epics', 'Features', 'Tasks'];
const CYCLE_MS = 350;
const PAUSE_BEFORE_CONNECTED = 400;
const CONNECTED_HOLD = 1200;
const FADE_OUT_MS = 800;

export function SplashScreen({ onComplete }: { onComplete: () => void }) {
  const [wordIndex, setWordIndex] = useState(0);
  const [phase, setPhase] = useState<'cycling' | 'connected' | 'done'>('cycling');
  const [lineProgress, setLineProgress] = useState(0);

  // Animate the connector lines growing
  useEffect(() => {
    const start = performance.now();
    const duration = WORDS.length * CYCLE_MS + PAUSE_BEFORE_CONNECTED;
    let raf: number;
    const tick = (now: number) => {
      const elapsed = now - start;
      setLineProgress(Math.min(elapsed / duration, 1));
      if (elapsed < duration) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); };
  }, []);

  // Cycle through words
  useEffect(() => {
    if (phase !== 'cycling') return;
    if (wordIndex >= WORDS.length - 1) {
      const t = setTimeout(() => { setPhase('connected'); }, PAUSE_BEFORE_CONNECTED);
      return () => { clearTimeout(t); };
    }
    const t = setTimeout(() => { setWordIndex((i) => i + 1); }, CYCLE_MS);
    return () => { clearTimeout(t); };
  }, [wordIndex, phase]);

  // Hold "Connected" then fade out
  useEffect(() => {
    if (phase !== 'connected') return;
    const t = setTimeout(() => { setPhase('done'); }, CONNECTED_HOLD);
    return () => { clearTimeout(t); };
  }, [phase]);

  const handleFadeComplete = useCallback(() => {
    if (phase === 'done') onComplete();
  }, [phase, onComplete]);

  return (
    <AnimatePresence onExitComplete={handleFadeComplete}>
      {phase !== 'done' && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: FADE_OUT_MS / 1000, ease: 'easeInOut' }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-background"
        >
          <div className="w-full overflow-hidden flex items-center justify-center">
          <div className="flex items-center gap-0 select-none shrink-0 scale-[0.55] sm:scale-75 md:scale-100 origin-center">
            {/* Left — SpecTree */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="flex items-center gap-3 mr-8 shrink-0"
            >
              <img src="/toro-logo.svg" alt="" className="h-10" />
              <span
                className="text-3xl font-bold text-foreground tracking-tight"
                style={{ fontFamily: "'Golos Text', sans-serif" }}
              >
                SpecTree
              </span>
            </motion.div>

            {/* Center — Node connector */}
            <svg width="160" height="24" viewBox="0 0 160 24" className="mx-2 shrink-0">
              <motion.line
                x1="0" y1="12" x2="60" y2="12"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="1.5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: lineProgress }}
              />
              <motion.circle
                cx="64" cy="12" r="3"
                fill="none"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="1.5"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: lineProgress > 0.3 ? 1 : 0, scale: lineProgress > 0.3 ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              />
              <motion.g
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: lineProgress > 0.5 ? 1 : 0,
                  scale: lineProgress > 0.5 ? 1 : 0,
                }}
                transition={{ duration: 0.3 }}
              >
                <rect
                  x="74" y="6" width="12" height="12"
                  rx="2"
                  transform="rotate(45 80 12)"
                  fill="none"
                  stroke={phase === 'connected' ? '#ef4444' : 'rgba(255,255,255,0.6)'}
                  strokeWidth="1.5"
                  style={{ transition: 'stroke 0.4s ease' }}
                />
                <circle
                  cx="80" cy="12" r="2"
                  fill={phase === 'connected' ? '#ef4444' : 'rgba(255,255,255,0.4)'}
                  style={{ transition: 'fill 0.4s ease' }}
                />
              </motion.g>
              <motion.circle
                cx="96" cy="12" r="3"
                fill="none"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="1.5"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: lineProgress > 0.7 ? 1 : 0, scale: lineProgress > 0.7 ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              />
              <motion.line
                x1="100" y1="12" x2="160" y2="12"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="1.5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: Math.max(0, (lineProgress - 0.5) * 2) }}
              />
            </svg>

            {/* Right — Scrolling words */}
            <div className="ml-8 w-[180px] h-[48px] overflow-hidden relative">
              <div className="absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-background to-transparent z-10 pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />

              <AnimatePresence mode="popLayout">
                {phase === 'connected' ? (
                  <motion.div
                    key="connected"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="flex items-center h-[48px]"
                  >
                    <span className="text-3xl font-bold text-red-500 tracking-tight">
                      Connected
                    </span>
                  </motion.div>
                ) : (
                  <motion.div
                    key={`word-${String(wordIndex)}`}
                    initial={{ y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -24, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="flex items-center h-[48px]"
                  >
                    <span className="text-3xl font-bold text-muted-foreground tracking-tight">
                      {WORDS[wordIndex]}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>

              {phase === 'cycling' && (
                <>
                  <div className="absolute top-[-14px] left-0 text-xl font-bold text-muted-foreground/20 tracking-tight pointer-events-none">
                    {WORDS[Math.max(0, wordIndex - 1)] ?? ''}
                  </div>
                  <div className="absolute bottom-[-14px] left-0 text-xl font-bold text-muted-foreground/20 tracking-tight pointer-events-none">
                    {WORDS[Math.min(WORDS.length - 1, wordIndex + 1)] ?? ''}
                  </div>
                </>
              )}
            </div>
          </div>
          </div>

          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="absolute bottom-5 text-xs text-muted-foreground tracking-[0.2em] uppercase"
          >
            The Toro Company ©
          </motion.span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
